/**
 * DashboardRegistry - Registro de componentes de dashboard
 * 
 * Mapeia component_key do banco de dados para componentes React.
 * Permite adicionar novos dashboards apenas alterando este arquivo.
 * Suporta passagem de filtros globais para os dashboards.
 * 
 * @author Sistema
 * @version 1.5.0
 * RELEASE: 2026-10-18 v1.6.0 - Registra o dashboard de Ordem de Serviço (Visão Geral).
 * RELEASE: 2026-10-03 v1.5.0 - Registra a aba Vistoria do módulo Veículos.
 * RELEASE: 2026-10-03 v1.4.0 - Registra o dashboard de Veículos (aba Geral) e
 *          o repasse de `vehicleFilters`.
 * RELEASE: 2026-09-16 v1.3.0 - Registra as abas CRM Por Produto/Serviço e
 *          Por Origem.
 */

import React, { lazy, Suspense } from 'react';
import { Loader2 } from 'lucide-react';
import type { CrmOverviewFilters, PeriodFilter } from '@/hooks/useCrmOverviewFilters';
import type { VehiclesDashboardFilters } from '@/hooks/useVehiclesDashboardOverview';
import type { ServiceOrdersDashboardFilters } from '@/hooks/useServiceOrdersDashboardOverview';

// v1.6.0: Dashboard do módulo Ordem de Serviço (aba Visão Geral)
const ServiceOrdersOverviewDashboard = lazy(() => import('@/components/service-orders/dashboard/ServiceOrdersOverviewDashboard'));
const ServiceOrdersProvidersDashboard = lazy(() => import('@/components/service-orders/dashboard/ServiceOrdersProvidersDashboard'));
const ServiceOrdersVehiclesDashboard = lazy(() => import('@/components/service-orders/dashboard/ServiceOrdersVehiclesDashboard'));
const ServiceOrdersMovementsDashboard = lazy(() => import('@/components/service-orders/dashboard/ServiceOrdersMovementsDashboard'));
const ServiceOrdersRegionsDashboard = lazy(() => import('@/components/service-orders/dashboard/ServiceOrdersRegionsDashboard'));

// Dashboard do módulo Veículos (aba Geral)
const VehiclesOverviewDashboard = lazy(() => import('@/components/vehicles/dashboard/VehiclesOverviewDashboard'));
const VehiclesInspectionsDashboard = lazy(() => import('@/components/vehicles/dashboard/VehiclesInspectionsDashboard'));

// Lazy load dos componentes de dashboard para performance
const CrmDashboardOverview = lazy(() => import('@/components/crm/dashboard/CrmDashboardOverview'));
const SellerPerformanceDashboard = lazy(() => import('@/components/crm/dashboard/SellerPerformanceDashboard'));
const MyPerformanceDashboard = lazy(() => import('@/components/crm/dashboard/MyPerformanceDashboard'));
const CrmProductServiceDashboard = lazy(() => import('@/components/crm/dashboard/CrmProductServiceDashboard'));
// Aba "Por Recorrência" (indicadores de serviços recorrentes vendidos)
const CrmRecurrenceDashboard = lazy(() => import('@/components/crm/dashboard/CrmRecurrenceDashboard'));
const CrmOriginDashboard = lazy(() => import('@/components/crm/dashboard/CrmOriginDashboard'));
// Dashboard de Playbooks (Fase 4.1 — Playbook de Vendas)
const CrmPlaybookOverview = lazy(() => import('@/components/crm/dashboard/CrmPlaybookOverview'));
// Dashboard Financeiro
const FinancialDashboardTabs = lazy(() => import('@/components/financial/dashboard/FinancialDashboardTabs'));
// Dashboards de Conversas
const ConversationOverviewDashboard = lazy(() => import('@/components/dashboard/ConversationOverviewDashboard'));
const ServiceDashboard = lazy(() => import('@/components/dashboard/ServiceDashboard'));
// Dashboard de Tarefas
const TasksDashboardOverview = lazy(() => import('@/components/tasks/dashboard/TasksDashboardOverview'));
const MyTasksDashboard = lazy(() => import('@/components/tasks/dashboard/MyTasksDashboard'));
// Dashboard de Indicação
const ReferralOverviewDashboard = lazy(() => import('@/components/referral/dashboard/ReferralOverviewDashboard'));
// Dashboard "Minhas Atividades" (módulo Atividades)
const MyActivitiesDashboard = lazy(() => import('@/components/activities/dashboard/MyActivitiesDashboard'));
// Dashboard "Visão Geral Atividades" (módulo Atividades — multi‑vendedor)
const TeamActivitiesDashboard = lazy(() => import('@/components/activities/dashboard/team/TeamActivitiesDashboard'));
// Dashboard "Por Solicitações" (módulo Retenção)
const RetentionRequestsDashboard = lazy(() => import('@/components/retention/dashboard/RetentionRequestsDashboard'));
// Dashboard "Por Recebimento" (módulo Retenção — financeiro vinculado às tarefas)
const RetentionReceivablesDashboard = lazy(() => import('@/components/retention/dashboard/RetentionReceivablesDashboard'));
// Dashboard "Por Performance" (módulo Retenção — performance dos usuários)
const RetentionPerformanceDashboard = lazy(() => import('@/components/retention/dashboard/RetentionPerformanceDashboard'));
// Dashboards do módulo Jurídico (Geral | Por Recebimento | Por Performance)
const LegalOverviewDashboard = lazy(() => import('@/components/legal/dashboard/LegalOverviewDashboard'));
const LegalReceivablesDashboard = lazy(() => import('@/components/legal/dashboard/LegalReceivablesDashboard'));
const LegalPerformanceDashboard = lazy(() => import('@/components/legal/dashboard/LegalPerformanceDashboard'));

// Componente de fallback durante carregamento
function DashboardLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}

// Mapeamento de component_key para componente React
const DASHBOARD_COMPONENTS: Record<string, React.LazyExoticComponent<React.ComponentType<any>>> = {
  'crm_overview': CrmDashboardOverview,
  'seller_performance': SellerPerformanceDashboard,
  'crm_product_service': CrmProductServiceDashboard,
  'crm_recurrence': CrmRecurrenceDashboard,
  'crm_origin': CrmOriginDashboard,
  'my_performance': MyPerformanceDashboard,
  'crm_playbook_overview': CrmPlaybookOverview,
  'financial_overview': FinancialDashboardTabs,
  'conversation_overview': ConversationOverviewDashboard,
  'service_dashboard': ServiceDashboard,
  'tasks_overview': TasksDashboardOverview,
  'tasks_my_tasks': MyTasksDashboard,
  'referral_overview': ReferralOverviewDashboard,
  'activities_my_activities': MyActivitiesDashboard,
  'activities_team_overview': TeamActivitiesDashboard,
  'retention_requests_overview': RetentionRequestsDashboard,
  'retention_receivables': RetentionReceivablesDashboard,
  'retention_performance': RetentionPerformanceDashboard,
  'legal_overview': LegalOverviewDashboard,
  'legal_receivables': LegalReceivablesDashboard,
  'legal_performance': LegalPerformanceDashboard,
  // v1.4.0: módulo Veículos (aba Geral)
  'vehicles_overview': VehiclesOverviewDashboard,
  'vehicles_inspections_overview': VehiclesInspectionsDashboard,
  'service_orders_overview': ServiceOrdersOverviewDashboard,
  // BR-OS-058: abas Por Prestador e Por Região
  'service_orders_providers': ServiceOrdersProvidersDashboard,
  'service_orders_regions': ServiceOrdersRegionsDashboard,
  'service_orders_vehicles': ServiceOrdersVehiclesDashboard,
  'service_orders_movements': ServiceOrdersMovementsDashboard,
};

/** Filtros de conversas passados do header */
interface ConversationFilters {
  periodFilter: PeriodFilter;
  inboxId: string | null;
  /** IDs de inboxes resolvidos a partir do grupo selecionado (null = sem filtro de grupo) */
  inboxIds?: string[] | null;
}


/** Filtros do dashboard de Indicação (período custom) */
interface ReferralFilters {
  startDate?: Date;
  endDate?: Date;
}

/**
 * Props para o renderizador de dashboards
 */
interface DashboardRendererProps {
  /** Chave do componente a ser renderizado */
  componentKey: string;
  /** Props adicionais para o componente */
  dashboardId?: string;
  /** Filtros globais do CRM (período, workspace, vendedor) */
  filters?: CrmOverviewFilters;
  /** Filtros de conversas (período, inbox) */
  conversationFilters?: ConversationFilters;
  /** Filtros do dashboard de Indicação */
  referralFilters?: ReferralFilters;
  /** v1.4.0: filtros do dashboard de Veículos (período, tipo, categoria, status) */
  vehicleFilters?: VehiclesDashboardFilters;
  /** v1.6.0: filtros do dashboard de O.S. (período, tipo, prestador, categoria) */
  serviceOrderFilters?: ServiceOrdersDashboardFilters;
}

/**
 * Renderiza o componente de dashboard baseado na chave
 * Passa os filtros globais para o componente selecionado
 */
export function DashboardRenderer({ componentKey, dashboardId, filters, conversationFilters, referralFilters, vehicleFilters, serviceOrderFilters }: DashboardRendererProps) {
  const Component = DASHBOARD_COMPONENTS[componentKey];

  if (!Component) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
        <p className="text-lg font-medium">Dashboard não encontrado</p>
        <p className="text-sm">O componente "{componentKey}" não está registrado.</p>
      </div>
    );
  }

  return (
    <Suspense fallback={<DashboardLoader />}>
      <Component
        dashboardId={dashboardId}
        filters={componentKey === 'referral_overview' ? referralFilters : filters}
        conversationFilters={conversationFilters}
        vehicleFilters={vehicleFilters}
        serviceOrderFilters={serviceOrderFilters}
      />
    </Suspense>
  );
}

/**
 * Verifica se um component_key está registrado
 */
export function isDashboardRegistered(componentKey: string): boolean {
  return componentKey in DASHBOARD_COMPONENTS;
}

/**
 * Lista todas as chaves de dashboard registradas
 */
export function getRegisteredDashboardKeys(): string[] {
  return Object.keys(DASHBOARD_COMPONENTS);
}
